import React, { useState } from "react";
import { Merchant, Transaction, Util } from "model";
import { DisabledPrompt, useModel, useParams, useRoute, BCMSCurrency } from "@bcm-dev/bcms-vite";

import MonthTabsContainer from "components/shared/containers/MonthTabsContainer";
import { ImageAsset, SVGAsset } from "res";
import TransactionView from "components/shared/views/BaseRowView/components/TransactionView";
import { MainContentSection } from "components/shared/containers/MainContentContainer";
import { GroupDropdownView } from "../..";

import "./styles.css";

/**
 * @function WhatView
 * @return {React.Component}
 * @exported true
 * @description The main component for WhatView
 * @example
 * <WhatView />
 */

export default function WhatView(props) {
  // Initalize all merchants and get the name and logo of each group that exists
  const groups = Merchant.initAll().reduce((result, merchant) => {
    // Get the logo and remove dashes and capitalize each word
    const logo = merchant.groupLogo.split("-").reduce((result, item) => `${ result }${ item.charAt(0).toUpperCase() }${ item.slice(1) }`, "");
    const info = { "name" : merchant.group, logo };
    // If the group hasn't already been added, then add it to the result
    if(result.find(item => item.name === merchant.group) === undefined) { result.push(info); }
    return result;
  }, []);
  // Map over the existing groups creating a new CategoryGroupDropdown component
  const dropdowns = groups.map((group, index) => {
      return <CategoryGroupDropdown key={ `Group_${ index }` } name={ group.name } logo={ group.logo } />
  }, []);

  const model = useModel();

  // Set the default category and get the card id from the parameters
  const category = props.category ?? "Department Store";
  const cardID = useParams().cardID ?? Util.getDefaultCardID(model);
  // Filter all transactions by category and card then calculate the total amount
  const totalAmount = Transaction.initAll()
    .filter(item => item.card.id === cardID && item.merchant.group === category, [])
    .reduce((result, item) => {
      const amount = parseFloat(item.amount.amount);
      return (result + (item.credit === "true" ? -amount : amount));
    }, 0);
  const amountLabel = `${ totalAmount < 0 ? "+" : "" }${ BCMSCurrency.toCurrency(totalAmount) }`

  return (
    <div className="what-view">
      <div className="info-group custom-grid-container">
        <div className="text-right icon-container flex-container align-bottom-container all-space-around-container">
          <DisabledPrompt>
            <div className="clickable more-info">
              <SVGAsset id="InformationIcon" />
            </div>
          </DisabledPrompt>
        </div>
        <div className="flex-container flex-vertical graph-container align-center-container">
          <div className="chart-asset">
            <ImageAsset id="WhatGraphImage" className="graph" />
          </div>
          <div className="inner-graph text-center">
            <ImageAsset id="ShoppingBagIcon" />
            <p className="total"><b>{ amountLabel }</b></p>
            <p className="sub-text" data-point="Spend_Insights_What_Category">{ category }</p>
          </div>
        </div>
        <MonthTabsContainer />
      </div>

      <MainContentSection text="Recent Transactions">
        { dropdowns }
      </MainContentSection>
    </div>
  );
}

/**
 * @function CategoryGroupDropdown
 * @param {STRING} name - The name of the group
 * @param {STRING} logo - The logo of the group
 * @returns {React.Component}
 * @exported false
 * @description Creates a toggleable dropdown for the group passed in through the properties
 * @example
 * <CategoryGroupDropdown name="Group" logo="GroupLogo" />
 */
function CategoryGroupDropdown(props) {
  // Set defaults for the name, logo and card id
  const navigate = useRoute();
  const name = props.name ?? "";
  const logo = props.logo ?? "";
  const cardID = useParams().cardID ?? "0";
  // Initalize a new state to control if the dropdown content should be shown or not
  const [ state, setState ] = useState(false);
  const toggle = evt => setState(!state);
  // Initalize all transactions and filter by the group name and card id
  const transactions = Transaction.initAll().filter(item => item.merchant.group === name && item.card.id === cardID, []);
  // For all transactions in the group and up the total sum
  const totalAmount = transactions.reduce((result, item) => {
    const amount = parseFloat(item.amount.amount);
    return (result + ( item.credit === "true" ? -amount : amount ));
  }, 0);

  // For each transaction create a new TransactionView component
  const transactionItems = transactions.map(transaction => {
      const isCredit = transaction.credit === "true";
      const className = isCredit ? "is-credit" : "";
      const amount = `${ isCredit ? "+" : "" }${ transaction.amount.format() }`;
      const onClick = transaction.merchant.name !== "Payment" ?
       evt => navigate(`/home/transactiondetails/${cardID}/${transaction.id}`)
       : () => {};

      return (
        <TransactionView
          key={ `Transaction_${ transaction.id }` }
          billerName={ transaction.merchant.name }
          date={ transaction.purchaseDate.format("M d, Y") }
          positiveClassName={ className }
          amount={ amount }
          leftIcon={ transaction.merchant.logo }
          onClick={ onClick } />
      );
  }, []);

  return (
      <GroupDropdownView
          onClick={ toggle }
          state={ state }
          logo={ <ImageAsset id={ logo } className="group-icon" /> }
          name={ name }
          className={ `${ totalAmount < 0 ? "amount-is-credit" : "" }` }
          totalAmount={ BCMSCurrency.toCurrency(totalAmount).replace("-", "+") }>

          { transactionItems }
      </GroupDropdownView>
  );
}